import { useNavigate } from 'react-router';
import { useDialog } from '@good-season/ui';

import { forgetSignedIn, pendingEvents } from '../scorebook/db.js';
import { clearSignedIn } from '../scorebook/owner.js';
import { bounded, noteSignOut, sendSignOut } from './client.js';

/** What happens to plays left on the phone. Only what the code does (ADR-0090): the server refuses them to anybody who cannot score for the team. */
const LEFT_BEHIND =
  'They send when someone who can score for this team signs in here and opens a scorebook, and count as sent by that person. To keep them under your name, stay signed in until they send. With no signal, this phone signs you out now and tells the server the next time it connects.';

/**
 * Sign out, from every screen the shell offers it on.
 *
 * Six screens each carried their own copy of this. It is one now because
 * signing out also has to close the scorebook to the next person on the phone
 * (#43), and a seventh screen copying the old version would quietly leave a
 * child's roster readable.
 *
 * It works with no signal. The sign-out is recorded on the phone first, and
 * until the server confirms it the app treats the phone as signed out and
 * sends it before any other request (`api()`, ADR-0090).
 *
 * A play that has not reached the server is never deleted. The person is told
 * it is there and may stay signed in; if they go anyway, it stays on the
 * phone and the book stays closed to anybody but them. The plays themselves
 * send under the next person who can score for that team and opens a book.
 *
 * Every store call is bounded, so an IndexedDB that never answers cannot keep
 * the roster or the allergy card on screen for good: this always navigates to
 * `/signin`. On such a store that takes up to 2 s before the warning, then up
 * to 16 s after the person confirms (two 2 s store steps, the sign-out POST at
 * up to 8 s, then two 2 s tries to clear the record once it lands). ADR-0090.
 */
export function useSignOut(): () => Promise<void> {
  const navigate = useNavigate();
  const dialog = useDialog();

  return async () => {
    // A count that never arrives, or a read that fails, still shows the warning: a WebKit
    // connection lost after open rejects every read while the plays are still on disk.
    const unsent = (await bounded(pendingEvents()))?.length;
    if (unsent !== 0) {
      const go = await dialog.confirm({
        title:
          unsent === undefined
            ? 'This phone may have plays that have not reached the server.'
            : `${String(unsent)} ${unsent === 1 ? 'play has' : 'plays have'} not reached the server.`,
        body: `Signing out keeps ${unsent === undefined ? 'any' : 'them'} on this phone. ${LEFT_BEHIND}`,
        confirmLabel: 'Sign out',
        cancelLabel: 'Stay signed in',
      });
      if (!go) return;
    }

    // Recorded BEFORE any store call or request that may never land: from
    // here on nothing treats this phone as signed in, signal or not.
    noteSignOut();
    let saved: number | undefined;
    let sent = false;
    let recordKept = false;
    try {
      saved = await bounded(forgetSignedIn());
      // A failure with the write saved leaves it for the first request with
      // signal, and there is nothing to tell the person: they have left.
      sent = await sendSignOut().then(
        () => true,
        () => false,
      );
      // Landed, but the store may still name this person: clear that record again.
      if (sent && saved === undefined) recordKept = !(await clearSignedIn());
    } finally {
      // Neither saved nor sent, it lives only in this page: `/signin` says so (SGNSAV).
      // Sent, with the record still naming them, `/signin` says that instead (SGNREC).
      void navigate('/signin', {
        replace: true,
        state:
          saved === undefined && !sent
            ? { signOutUnsaved: true }
            : recordKept
              ? { signOutRecordKept: true }
              : null,
      });
    }
  };
}
